import React, { FC, useEffect, useState } from "react";
import { Stack, styled, Typography } from "@mui/material";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import SaveIcon from "@mui/icons-material/Save";
import { AppRoutes } from "@/routes/config";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { importTalentsApi, useTalentOptions } from "@/api/services/main/talent";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { TalentAfterImport } from "@/features/talent/components/talent-import/TalentAfterImport";
import { divider, orange, state } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { sanitizeInput } from "@/utils/pieces";
import { Form } from "@/components/elements/Form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validator } from "@/utils/validator";
import { TApiError, TApiErrorField } from "@/types/api";
import { rounded } from "@/theme/variables";
import { LoadingArea } from "@/components/elements/Loading/Loading";

type RowError = {
  row: number;
  messages: string[];
};

type TTalent = {
  fullName?: string | null;
  nickName?: string | null;
  birthday?: string | null;
  id: string;
};

export const TalentImportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { talents } = location.state || {};
  const totalRow = talents?.length ?? 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { options: listTalent, isLoading } = useTalentOptions();
  const [errors, setErrors] = useState<RowError[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const isShowError = errors.length > 0 && !isUpdateMode;

  // Resolve name key + label for duplicate detection.
  // Priority: fullName > nickName. Row with neither name is skipped.
  const getNameKey = (t: TTalent): { key: string; label: string; name: string } | null => {
    if (t.fullName?.trim()) {
      const key = `fullName:${sanitizeInput(t.fullName).toUpperCase()}-${t.birthday ?? ""}`;
      return { key, label: "氏名", name: t.fullName };
    }
    if (t.nickName?.trim()) {
      const key = `nickName:${sanitizeInput(t.nickName).toUpperCase()}-${t.birthday ?? ""}`;
      return { key, label: "呼び名", name: t.nickName };
    }
    return null;
  };

  const checkImport = (
    talents: TTalent[],
    dbList: { fullName?: string | null; nickName?: string | null; birthday: string }[],
  ) => {
    // Build in-file duplicate map
    const keyToIndices: Record<string, number[]> = {};
    talents.forEach((t, idx) => {
      const entry = getNameKey(t);
      if (!entry) return;
      if (!keyToIndices[entry.key]) keyToIndices[entry.key] = [];
      keyToIndices[entry.key].push(idx);
    });

    const errorsByRow: RowError[] = [];

    talents.forEach((talent, idx) => {
      const rowNum = idx + 1;
      const entry = getNameKey(talent);
      if (!entry) return;

      const list: string[] = [];
      const indices = keyToIndices[entry.key];

      // In-file duplicates
      if (indices && indices.length > 1) {
        const rows = indices.map((i) => `${i + 1}行目`).join("、");
        list.push(`${rows}の項目「${entry.label}」（${entry.name}）が重複しています。`);
      }

      // DB duplicates — check by fullName or nickName depending on which is present
      const existsInDB = dbList.some((db) => {
        if (talent.fullName?.trim()) {
          const nameMatch =
            sanitizeInput(String(db.fullName ?? "")).toUpperCase() ===
            sanitizeInput(talent.fullName).toUpperCase();
          if (!nameMatch) return false;
          if (!talent.birthday) return true;
          return db.birthday === dayjs(talent.birthday).format(DATE_FORMAT_FOR_API);
        } else if (talent.nickName?.trim()) {
          const nickMatch =
            sanitizeInput(String(db.nickName ?? "")).toUpperCase() ===
            sanitizeInput(talent.nickName).toUpperCase();
          if (!nickMatch) return false;
          if (!talent.birthday) return true;
          return db.birthday === dayjs(talent.birthday).format(DATE_FORMAT_FOR_API);
        }
        return false;
      });

      if (existsInDB) {
        list.push(`${rowNum}行目の必須項目「${entry.label}」(${entry.name})が既に登録されています。`);
      }

      if (list.length > 0) {
        errorsByRow.push({ row: rowNum, messages: Array.from(new Set(list)) });
      }
    });

    // Deduplicate identical messages across rows
    const seen = new Set<string>();
    return errorsByRow.map((item) => ({
      row: item.row,
      messages: item.messages.filter((msg) => {
        if (seen.has(msg)) return false;
        seen.add(msg);
        return true;
      }),
    }));
  };

  useEffect(() => {
    if (!isLoading) {
      const listTalentDB = listTalent.map((talent) => ({
        fullName: talent.fullName,
        nickName: talent.nickName,
        birthday: dayjs(talent.birthday).format(DATE_FORMAT_FOR_API),
      }));
      const newErrors = checkImport(talents, listTalentDB);
      const isUpdate = talents.some((talent: TTalent) => !!talent.id);
      setIsUpdateMode(isUpdate);
      setErrors(newErrors);
      setIsReady(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!talents) navigate(AppRoutes.talentList);
    methods.setValue("hasValue", true, {
      shouldDirty: true,
    });
  });

  const handleSaveData = async () => {
    if (talents.length === 0) return;
    setIsSubmitting(true);
    try {
      await importTalentsApi(talents);
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.talentList);
    } catch (err) {
      let message: string;
      const error = err as TApiError;

      if (Array.isArray(error.error)) {
        message = error.error.map((e: TApiErrorField) => `${e.property}: ${e.message}`).join("\n");
      } else {
        message = error.error || error.message || API_UNKNOWN_ERROR_MESSAGE;
      }

      toast.error(message);
    }
    setIsSubmitting(false);
  };

  const TitleInnerBox: FC = () => {
    return (
      <Typography variant="sub16Semi">
        ファイルアップロード結果：<Typography variant="head20Semi">{totalRow}</Typography> 件
      </Typography>
    );
  };
  const formSchema = validator.object().shape({
    hasValue: validator.boolean().required(),
  });
  type TForm = {
    hasValue: boolean;
  };
  const methods = useForm<TForm>({
    defaultValues: {
      hasValue: false,
    },
    mode: "all",
    reValidateMode: "onBlur",
    resolver: yupResolver(formSchema),
  });

  return (
    <>
      <PageWrapper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <TopPage title={isUpdateMode ? "複数人材一括更新" : "複数人材登録"} enablePageBack={false} />
        </Stack>
        <Content>
          <InnerBox borderColor={divider.high} padding={3} mt={2} border="none" showDivider={false} title={<TitleInnerBox />}>
            {!isReady ? (
              <LoadingArea height={300} />
            ) : (
              <>
                <Stack flex={1} gap={3}>
                  <TalentAfterImport rows={talents ?? []} isLoading={false} />
                </Stack>
                <Form
                  methods={methods}
                  onSubmit={() => {}}
                  schema={formSchema}
                  enableBlocker={!isSubmitting}
                  customMessageLeave="インポートが終了しておりませんが戻ってもよろしいですか？"
                >
                  {isShowError && (
                    <Stack
                      sx={{ mt: "32px", p: "8px", background: state.incomplete_secondary, color: orange[80], maxHeight: "160px", overflowY: "auto" }}
                      borderRadius={rounded.xs}
                    >
                      {errors.map((err, index) => (
                        <Typography key={index} component="p" variant="cap12">
                          {err.messages.map((m, i) => (
                            <li key={i}>{m}</li>
                          ))}
                        </Typography>
                      ))}
                      <Typography component="p" variant="cap12">
                        修正が必要な場合は修正後に再インポートしてください。
                      </Typography>
                    </Stack>
                  )}
                </Form>
              </>
            )}
          </InnerBox>
        </Content>

        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button size="sm" variant="ghost" onClick={() => navigate(AppRoutes.talentList)}>
              戻る
            </Button>
            <Stack direction="row" gap={3}>
              <Button size="sm" variant="primary" disabled={isSubmitting || isLoading} onClick={handleSaveData} startIcon={<SaveIcon />}>
                一括保存
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
      </PageWrapper>
    </>
  );
};

const PageWrapper = styled(Stack)`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const Content = styled(Stack)`
  flex: 1;
`;
